import clsx from "clsx";
import "./SupportBanner.scss";
import { Col, Row } from "@/components";
import { useMemo } from "react";
import { CTA_COMPONENTS, SupportBannerLink } from "./ctaComponents";
import { HeadingLevel } from "@/types/headings";

type SupportBannerProps = {
  bannerType?: "filled" | "outline";
  title: string;
  description?: string;
  titleTag?: HeadingLevel;
  mainLayout?: "layout1" | "layout2";
  className?: string;
  classNameTitle?: string;
  classNameDescription?: string;
  links?: SupportBannerLink[];
  asLink?: "a" | React.ComponentType<any>;
  children?: React.ReactNode;
};

export const SupportBanner = ({
  bannerType = "outline",
  title,
  description = "",
  titleTag: TitleTag = "h1",
  mainLayout = "layout1",
  className = "",
  classNameTitle = "",
  classNameDescription = "",
  links = [],
  asLink = "a",
  children,
}: SupportBannerProps) => {
  const LinkComponent = asLink;
  const renderCTA = useMemo(() => {
    if (!links || links.length === 0) return null;
    return links.map((link, index) => {
      const variant =
        link.variant ?? (link.href ? "outlineButtonHref" : "button");
      const Renderer = CTA_COMPONENTS(LinkComponent)[variant];
      return (
        <div key={index} className="ui-banner-link">
          {Renderer(link)}
        </div>
      );
    });
  }, [links, LinkComponent]);

  return (
    <Col
      br={"x4"}
      gap={"x16"}
      className={clsx(
        className,
        bannerType === "outline" ? "ui-outline" : "bg-lightblue-a03",
        "ui-banner shadow ",
      )}
    >
      <TitleTag
        className={clsx(
          "ui-banner-title text-slate-20",
          classNameTitle || "fs-4 fw-bold",
        )}
      >
        {title}
      </TitleTag>
      <main
        className={clsx(
          mainLayout === "layout1"
            ? "ui-banner-content-layout1 fs-6 text-secondary justify-content-between "
            : "ui-banner-content-layout2",
          "ui-banner-content fs-6 text-secondary w-100",
        )}
      >
        <div
          className={clsx(
            classNameDescription,
            mainLayout === "layout1" ? "ui-banner-description" : "",
          )}
        >
          {description}
        </div>

        <Row className="ui-banner-row">
          {children}
          <div className={clsx("ui-banner-cta")}>{renderCTA}</div>
        </Row>
      </main>
    </Col>
  );
};
